/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useState } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';

import {
  Button,
  BackLink,
  Redirect,
  useNavigation,
  useParams,
} from '../Navigation';
import {
  ApolloError,
  Heading,
  HStack,
  LoadingIndicator,
  Spacer,
  Text,
  VStack,
} from '../../core-ui';

import EmailPreview from './EmailPreview';
import RecipientsDialog from './RecipientsDialog';

import {
  GetJob,
  GetJobVariables,
  GetJob_job as Job,
} from './__generated__/GetJob';
import { StartSend, StartSendVariables } from './__generated__/StartSend';
import { useShowApolloError } from '../../support/graphql/useShowApolloError';

const GET_JOB = gql`
  query GetJob($id: String!) {
    job(id: $id) {
      id
      rendered {
        type
        value
      }
      recipients {
        id
        name
        email
        vendor
        status
      }
      email {
        id
        subject
        status
      }
    }
  }
`;

const START_SEND = gql`
  mutation StartSend($jobId: String!) {
    startSend(jobId: $jobId) {
      result
    }
  }
`;

function SendPreviewPage(props: { job: Job }) {
  const {
    id,
    email: { subject },
    recipients,
    rendered,
  } = props.job;
  const [showRecipients, setShowRecipients] = useState(false);
  const showApolloError = useShowApolloError();
  const { navigate } = useNavigation();
  const [startSend] = useMutation<StartSend, StartSendVariables>(START_SEND, {
    onError: showApolloError,
    onCompleted: () => {
      navigate('/mailer/jobs/:id/progress', { id });
    },
  });
  return (
    <>
      <VStack align="stretch" px={10} py={2}>
        <BackLink to={['/mailer/emails']} alignSelf="start" />
        <HStack>
          <Heading as="h2" size="lg">
            {subject}
          </Heading>
          <Spacer />
          <Button
            variant="primary"
            onClick={() => {
              startSend({ variables: { jobId: id } });
            }}
          >
            {t('Send Now')}
          </Button>
        </HStack>
        <Text>
          {t.jsx('To: <link>{count} Recipient(s)</link>', {
            count: recipients.length,
            link: (content) => (
              <Button variant="link" onClick={() => setShowRecipients(true)}>
                {content}
              </Button>
            ),
          })}
        </Text>
      </VStack>
      <EmailPreview html={rendered.value} />
      <RecipientsDialog
        isOpen={showRecipients}
        recipients={recipients}
        onClosePress={() => setShowRecipients(false)}
      />
    </>
  );
}

export default function SendPreviewPageContainer() {
  const params = useParams();
  const { error, data } = useQuery<GetJob, GetJobVariables>(GET_JOB, {
    variables: { id: params.id || '' },
    fetchPolicy: 'network-only',
  });
  if (error) {
    return <ApolloError error={error} />;
  }
  if (!data) {
    return <LoadingIndicator />;
  }
  if (!data.job) {
    // TODO: Do something better here.
    return <Text>{t('Not Found')}</Text>;
  }
  const { job } = data;
  const { email } = job;
  if (email.status === 'SENDING') {
    return <Redirect to={['/mailer/jobs/:id/progress', { id: job.id }]} />;
  }
  if (email.status === 'SENT') {
    return <Redirect to={['/mailer/jobs/:id/summary', { id: job.id }]} />;
  }
  return <SendPreviewPage job={job} />;
}
